import { useEffect, useState } from "react";
import { FaCheck, FaTimes, FaWhatsapp } from "react-icons/fa";
import { whatsAppService } from "../../Api/Services/whatsAppService";
import { WhatsAppMessageStatus } from "../../Enums/WhatsAppMessageStatus";
import { WhatsAppTemplateType } from "../../Enums/WhatsAppTemplateType";
import { WhatsAppMessage } from "../../Models/WhatsApp";
import { formatDateTimeToBR, formatPhoneNumber } from "../../Utils/DataFormatters";
import { getWhatsAppMessageStatusLabel, getWhatsAppTemplateTypeLabel } from "../../Utils/Enums";
import { Select } from "../Select/Select";
import Pagination from "../Pagination/Pagination";

const PAGE_SIZE = 20;
const ALL = "all";

const TYPE_OPTIONS = [
  { value: ALL, label: "Todas as mensagens" },
  ...Object.values(WhatsAppTemplateType)
    .filter((value): value is WhatsAppTemplateType => typeof value === "number")
    .map((value) => ({ value, label: getWhatsAppTemplateTypeLabel(value) }))
];

const STATUS_OPTIONS = [
  { value: ALL, label: "Todos os status" },
  { value: WhatsAppMessageStatus.Sent, label: getWhatsAppMessageStatusLabel(WhatsAppMessageStatus.Sent) },
  { value: WhatsAppMessageStatus.Failed, label: getWhatsAppMessageStatusLabel(WhatsAppMessageStatus.Failed) }
];

// Recipients are stored in international form (5551999998888)
const formatRecipient = (recipient: string) => {
  if (!recipient) {
    return "Sem telefone";
  }
  const local = recipient.startsWith("55") && recipient.length >= 12 ? recipient.slice(2) : recipient;
  return formatPhoneNumber(local);
};

const StatusIcon = ({ message }: { message: WhatsAppMessage }) =>
  message.status === WhatsAppMessageStatus.Sent ? (
    <FaCheck className="text-green-600 text-xs" title={getWhatsAppMessageStatusLabel(message.status)} />
  ) : (
    <FaTimes className="text-red-600 text-xs" title={message.errorMessage ?? getWhatsAppMessageStatusLabel(message.status)} />
  );

const WhatsAppMessageHistory = () => {
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [templateType, setTemplateType] = useState<WhatsAppTemplateType | null>(null);
  const [status, setStatus] = useState<WhatsAppMessageStatus | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const page = await whatsAppService.getMessages({ page: currentPage, pageSize: PAGE_SIZE, templateType, status });
        if (!cancelled) {
          setMessages(page.items);
          setTotalPages(Math.max(page.totalPages, 1));
          setTotalItems(page.totalItems);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, templateType, status]);

  const handleTypeChange = (value: string) => {
    setTemplateType(value === ALL ? null : (Number(value) as WhatsAppTemplateType));
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value === ALL ? null : (Number(value) as WhatsAppMessageStatus));
    setCurrentPage(1);
  };

  return (
    <div className="bg-white border rounded shadow-sm flex flex-col min-h-[24rem] lg:sticky lg:top-4 lg:h-[calc(100dvh-8rem)]">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b rounded-t bg-primary-120">
        <h2 className="font-semibold">{"Histórico de mensagens"}</h2>
        <span className="text-xs text-gray-600">
          {totalItems === 1 ? "1 mensagem" : `${totalItems} mensagens`}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-4 py-3 border-b">
        <Select
          name="templateType"
          label="Mensagem"
          value={templateType ?? ALL}
          onChange={(e) => handleTypeChange(e.target.value)}
          options={TYPE_OPTIONS}
          className="sm:w-full"
        />
        <Select
          name="status"
          label="Status"
          value={status ?? ALL}
          onChange={(e) => handleStatusChange(e.target.value)}
          options={STATUS_OPTIONS}
          className="sm:w-full"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 flex flex-col">
        {isLoading && messages.length === 0 ? (
          <div className="flex flex-col">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse py-3 border-b">
                <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500 py-16">
            <FaWhatsapp className="text-3xl" />
            <p className="text-center text-sm">{"Nenhuma mensagem enviada ainda"}</p>
          </div>
        ) : (
          <div className={`flex flex-col ${isLoading ? "opacity-60 pointer-events-none" : ""}`}>
            {messages.map((message) => {
              const isExpanded = expandedId === message.id;
              return (
                <button
                  key={message.id}
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : message.id)}
                  title={isExpanded ? "Recolher" : "Ver mensagem completa"}
                  className="text-left py-3 border-b last:border-b-0 hover:bg-gray-50 -mx-4 px-4"
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {message.recipientName ?? formatRecipient(message.recipient)}
                      </span>
                      {message.recipientName && (
                        <span className="text-xs text-gray-500 whitespace-nowrap">{formatRecipient(message.recipient)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <StatusIcon message={message} />
                      <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTimeToBR(message.createdAt)}</span>
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{getWhatsAppTemplateTypeLabel(message.templateType)}</p>
                  <p className={`text-sm text-gray-600 ${isExpanded ? "whitespace-pre-line break-words" : "line-clamp-2"}`}>
                    {message.body}
                  </p>
                  {message.errorMessage && <p className="text-xs text-red-700 mt-1">{message.errorMessage}</p>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="border-t px-4 py-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default WhatsAppMessageHistory;
